import { AlertTriangle, Construction, Inbox, RefreshCw, Search, TrendingDown, TrendingUp } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { ApiError } from '@/api/client'
import type { ChargerStatus, ConnectorStatus, SessionStatus } from '@/api/types'
import { Badge, Card, Skeleton, type Tone } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form'
import { percent } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { cn, errorText } from '@/lib/utils'

export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: { to: string; label: string } }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {back && (
          <Link to={back.to} className="mb-1 inline-block text-[13px] text-text-2 hover:text-text">
            ← {back.label}
          </Link>
        )}
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-text-2">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  trend,
  loading,
  tone,
  to,
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  icon?: ReactNode
  trend?: number | null
  loading?: boolean
  tone?: 'brand' | 'success' | 'danger' | 'warning'
  to?: string
}) {
  const body = (
    <Card className={cn('h-full p-4 transition-colors', to && 'hover:border-border-strong')}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-medium text-text-2">{label}</p>
        {icon && (
          <span
            className={cn(
              'grid size-8 place-items-center rounded-lg bg-surface-3 text-text-2 [&_svg]:size-4',
              tone === 'brand' && 'bg-brand-soft text-brand-ink',
              tone === 'success' && 'bg-success-soft text-success',
              tone === 'danger' && 'bg-danger-soft text-danger',
              tone === 'warning' && 'bg-warning-soft text-warning',
            )}
          >
            {icon}
          </span>
        )}
      </div>
      {loading ? <Skeleton className="mt-2 h-7 w-28" /> : <p className="tabular mt-1 text-xl leading-tight font-semibold tracking-tight [overflow-wrap:anywhere] sm:text-2xl">{value}</p>}
      {(hint || trend != null) && (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-text-2">
          {trend != null && Number.isFinite(trend) && (
            <span className={cn('inline-flex items-center gap-0.5 font-medium', trend >= 0 ? 'text-success' : 'text-danger')}>
              {trend >= 0 ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
              {percent(Math.abs(trend))}
            </span>
          )}
          {hint}
        </p>
      )}
    </Card>
  )
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  )
}

export function EmptyState({ title, body, action, icon }: { title: ReactNode; body?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <span className="mb-3 grid size-12 place-items-center rounded-2xl bg-surface-3 text-muted [&_svg]:size-6">{icon ?? <Inbox />}</span>
      <p className="font-medium">{title}</p>
      {body && <p className="mt-1 max-w-sm text-sm text-text-2">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n()
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <span className="mb-3 grid size-12 place-items-center rounded-2xl bg-danger-soft text-danger">
        <AlertTriangle className="size-6" />
      </span>
      <p className="font-medium">{t("Couldn't load this")}</p>
      <p className="mt-1 max-w-md text-sm text-text-2">{errorText(error)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw /> {t('Try again')}
        </Button>
      )}
    </div>
  )
}

/**
 * A screen that needs an endpoint the backend doesn't have yet. Shows what
 * the feature does and which item of docs/BACKEND_REQUESTS.md it waits for.
 */
export function PendingFeature({ code, title, body, children }: { code: string; title: string; body: ReactNode; children?: ReactNode }) {
  const { t } = useI18n()
  return (
    <Card className="overflow-hidden border-dashed">
      <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-start">
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-soft text-brand-ink">
          <Construction className="size-6" />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{title}</h2>
            <Badge tone="brand">{t('Waiting for backend · {code}', { code })}</Badge>
          </div>
          <div className="mt-2 max-w-2xl text-sm text-text-2">{body}</div>
          {children && <div className="mt-4">{children}</div>}
        </div>
      </div>
    </Card>
  )
}

/** Wraps a query result for a feature that may not exist on the server yet. */
export function isPending(error: unknown) {
  return error instanceof ApiError && error.isMissing
}

export function SearchBox({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={cn('relative w-full sm:w-72', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9" aria-label={placeholder} />
    </div>
  )
}

export function useDebounced<T>(value: T, ms = 350) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

// ── Status badges ───────────────────────────────────────────────────────
const CONNECTOR: Record<string, { tone: Tone; label: string }> = {
  Available: { tone: 'success', label: 'Available' },
  Preparing: { tone: 'info', label: 'Plugged in' },
  Charging: { tone: 'brand', label: 'Charging' },
  SuspendedEV: { tone: 'warning', label: 'Paused by car' },
  SuspendedEVSE: { tone: 'warning', label: 'Paused by charger' },
  Finishing: { tone: 'info', label: 'Finishing' },
  Reserved: { tone: 'info', label: 'Reserved' },
  Unavailable: { tone: 'neutral', label: 'Unavailable' },
  Faulted: { tone: 'danger', label: 'Faulted' },
}
export function connectorTone(status: ConnectorStatus) {
  return CONNECTOR[status] ?? { tone: 'neutral' as Tone, label: status }
}
export function ConnectorBadge({ status }: { status: ConnectorStatus }) {
  const { t } = useI18n()
  const s = connectorTone(status)
  return (
    <Badge tone={s.tone} dot>
      {t(s.label)}
    </Badge>
  )
}

export function ChargerBadge({ status }: { status: ChargerStatus }) {
  const { t } = useI18n()
  const tone: Tone = status === 'Online' || status === 'Available' ? 'success' : status === 'Faulted' ? 'danger' : status === 'Offline' ? 'neutral' : 'warning'
  return (
    <Badge tone={tone} dot>
      {t(status === 'Available' ? 'Online' : status)}
    </Badge>
  )
}

export function SessionBadge({ status }: { status: SessionStatus }) {
  const { t } = useI18n()
  const map: Record<string, Tone> = { active: 'brand', completed: 'success', interrupted: 'warning', stopped: 'neutral', failed: 'danger' }
  const label: Record<string, string> = { active: 'Charging', completed: 'Completed', interrupted: 'Interrupted', stopped: 'Stopped', failed: 'Failed' }
  return (
    <Badge tone={map[status] ?? 'neutral'} dot>
      {t(label[status] ?? status)}
    </Badge>
  )
}

export function KeyValue({ items, className }: { items: [ReactNode, ReactNode][]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-[minmax(120px,auto)_1fr] gap-x-6 gap-y-2.5 text-sm', className)}>
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-text-2">{k}</dt>
          <dd className="min-w-0 break-words">{v ?? '–'}</dd>
        </div>
      ))}
    </dl>
  )
}

export function LoadingBlock({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-5">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  )
}
